import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import AppSidebar from '../components/AppSidebar';
import FormattedContent from '../components/document/FormattedContent';
import { downloadAsWord, DRAFT_TYPE_LABELS } from '../components/document/wordExport';
import {
  ArrowLeft,
  ArrowRight,
  FileText,
  Sparkles,
  Search,
  PenTool,
  Clock,
  CheckCircle2,
  CheckCircle,
  Copy,
  MessageSquare,
  BookOpen,
  Briefcase,
  ExternalLink,
  AlertCircle,
  Lightbulb,
  FileCheck,
  RefreshCw,
  Send,
  Phone,
  Mail,
  User,
  HelpCircle,
  Scale,
  Download,
  Languages,
  FileDown,
  Loader2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DocumentView = () => {
  const { docId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  
  const [document, setDocument] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [summarizeProgress, setSummarizeProgress] = useState(0);
  const [isDrafting, setIsDrafting] = useState(false);
  const [isResearching, setIsResearching] = useState(false);
  
  const [activeTab, setActiveTab] = useState('summary');
  const [researchQuery, setResearchQuery] = useState('');
  const [researchResults, setResearchResults] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [selectedDraftType, setSelectedDraftType] = useState('reply');
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [showTranslated, setShowTranslated] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [isReprocessingHindi, setIsReprocessingHindi] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  // Agentic drafting 3-stage progress UI: { visible, stage, stagesDone[] }
  const [draftPipelineStatus, setDraftPipelineStatus] = useState({ visible: false, stage: null, stagesDone: [] });
  // Per-draft tab selection map: { [draft_id]: 'initial' | 'critique' | 'final' }
  const [draftTab, setDraftTab] = useState({});

  const fetchDocument = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/documents/${docId}`);
      setDocument(response.data);
    } catch (error) {
      console.error('Error fetching document:', error);
      toast.error('Failed to load document');
      navigate('/dashboard');
    } finally {
      setIsLoading(false);
    }
  }, [docId, navigate]);

  const fetchDrafts = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await axios.get(`${API}/documents/${docId}/drafts`);
      setDrafts(response.data);
      // Auto-resume polling for any drafts still 'running'. This recovers from
      // page reload / closed tab during the 5-15 min agentic pipeline run.
      const running = response.data.filter(d => d.status === 'running');
      running.forEach(d => _pollDraft(d.draft_id));
      if (running.length > 0) {
        setIsDrafting(true);
        setDraftPipelineStatus({
          visible: true,
          stage: 'drafter',
          stagesDone: [],
        });
        toast.info(`Resuming draft pipeline (${running.length} in progress)`);
      }
    } catch (error) {
      console.error('Error fetching drafts:', error);
    }
  }, [docId, isAdmin]);

  const fetchResearch = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await axios.get(`${API}/documents/${docId}/research`);
      setResearchResults(response.data);
    } catch (error) {
      console.error('Error fetching research:', error);
    }
  }, [docId, isAdmin]);

  useEffect(() => {
    fetchDocument();
    if (isAdmin) {
      fetchDrafts();
      fetchResearch();
    }
  }, [fetchDocument, fetchDrafts, fetchResearch, isAdmin]);

  // Poll for updates when document is still processing
  useEffect(() => {
    if (!document || document.status !== 'processing') return;
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/documents/${docId}`);
        if (response.data.status !== 'processing') {
          setDocument(response.data);
          clearInterval(interval);
          if (response.data.status === 'text_extracted') {
            toast.success('Document processing complete!');
          } else if (response.data.status === 'failed') {
            toast.error('Document processing failed.');
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document?.status, docId]);

  const handleSummarize = async () => {
    if (!isAdmin) return;
    setIsSummarizing(true);
    setSummarizeProgress(0);
    
    const progressInterval = setInterval(() => {
      setSummarizeProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 15;
      });
    }, 500);
    
    try {
      const response = await axios.post(`${API}/ai/summarize/${docId}`);
      setSummarizeProgress(100);
      setDocument(prev => ({ ...prev, summary: response.data.summary, status: 'completed' }));
      toast.success('Summary generated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate summary');
    } finally {
      clearInterval(progressInterval);
      setTimeout(() => {
        setIsSummarizing(false);
        setSummarizeProgress(0);
      }, 500);
    }
  };

  const handleDraft = async () => {
    if (isDrafting || !isAdmin) return;
    setIsDrafting(true);
    setDraftPipelineStatus({ visible: true, stage: 'drafter', stagesDone: [] });
    try {
      // 1. Kick off the agentic pipeline (returns immediately with draft_id + status: "running")
      const response = await axios.post(`${API}/ai/draft`, {
        doc_id: docId,
        draft_type: selectedDraftType,
        additional_instructions: additionalInstructions
      });
      const draftId = response.data.draft_id;
      const newDraft = response.data;
      setDrafts(prev => [newDraft, ...prev]);
      setAdditionalInstructions('');
      setActiveTab('history');
      // Kick off resumable polling. `_pollDraft` is idempotent and is also
      // called on page-reload for any drafts still in 'running' state.
      _pollDraft(draftId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start draft generation');
      setIsDrafting(false);
      setDraftPipelineStatus({ visible: false, stage: null, stagesDone: [] });
    }
  };

  // Polling loop for an in-progress agentic draft. Lives outside handleDraft so
  // it can be re-invoked from a useEffect on page load to AUTO-RESUME drafts that
  // are still 'running' from a previous session/tab (e.g. user closed the tab).
  // Timing:
  //   - polls every 6 s (was 5 s) — reduces request churn for ~15 min pipelines
  //   - hard cap at 250 polls (~25 min) — covers the largest observed pipeline
  //     (Nimesh Gadodia, 12.5 min) with healthy margin
  //   - milestone messages at 5 / 10 / 15 / 20 min so user knows it's still alive
  const _pollDraft = (draftId) => {
    const stageToProgressKey = {
      initial_draft: 'drafter',
      critique_1: 'critic',
      refined_draft: 'finalizer',
      critique_2: 'critic',
      final_draft: 'finalizer',
    };
    const maxPolls = 250; // 250 × 6 s = 25 min
    const milestones = { 50: 5, 100: 10, 150: 15, 200: 20 }; // poll# → minutes-elapsed message
    let polls = 0;
    let lastStageCount = 0;
    const interval = setInterval(async () => {
      polls += 1;
      if (milestones[polls]) {
        toast.info(`Still drafting (${milestones[polls]} min) — large notices can take 15-20 min. Feel free to keep this tab open or come back later.`);
      }
      try {
        const r = await axios.get(`${API}/ai/draft/${draftId}`);
        const updated = r.data;
        // Update progress strip based on most recent completed stage
        const stages = updated?.pipeline?.stages || [];
        if (stages.length !== lastStageCount) {
          lastStageCount = stages.length;
          const lastStage = stages[stages.length - 1];
          const stageKey = stageToProgressKey[lastStage?.name];
          const stagesDone = [...new Set(stages.map(s => stageToProgressKey[s.name]).filter(Boolean))];
          setDraftPipelineStatus({
            visible: true,
            stage: stageKey,
            stagesDone,
          });
        }
        // Replace the placeholder draft in the list with the latest version
        setDrafts(prev => {
          const idx = prev.findIndex(d => d.draft_id === draftId);
          if (idx === -1) return [updated, ...prev];
          const next = [...prev];
          next[idx] = updated;
          return next;
        });
        if (updated.status === 'completed') {
          clearInterval(interval);
          setIsDrafting(false);
          setDraftPipelineStatus({ visible: true, stage: 'done', stagesDone: ['drafter', 'critic', 'finalizer'] });
          // Surface mode/warning so user knows when the system saved cost.
          const mode = updated.pipeline?.mode;
          const warning = updated.pipeline?.warning;
          if (mode === 'compact') {
            toast.warning(warning || 'Used compact mode (skipped 2nd critique) — large initial draft.', { duration: 8000 });
          } else if (mode === 'partial') {
            toast.error(warning || 'Pipeline finished partially — LLM budget exhausted. Returning the best version so far.', { duration: 10000 });
          } else {
            toast.success('Senior-advocate draft ready!');
          }
          // Auto-hide progress strip after a beat so user can read the success state
          setTimeout(() => setDraftPipelineStatus({ visible: false, stage: null, stagesDone: [] }), 5000);
        } else if (updated.status === 'failed') {
          clearInterval(interval);
          setIsDrafting(false);
          setDraftPipelineStatus({ visible: false, stage: null, stagesDone: [] });
          toast.error(`Draft generation failed: ${updated.error || 'unknown error'}`);
        }
      } catch (pollErr) {
        // Transient — keep polling. Only stop on auth errors.
        if (pollErr.response?.status === 401 || pollErr.response?.status === 403) {
          clearInterval(interval);
          setIsDrafting(false);
          setDraftPipelineStatus({ visible: false, stage: null, stagesDone: [] });
          toast.error('Session expired. Please refresh.');
        }
      }
      if (polls >= maxPolls) {
        clearInterval(interval);
        setIsDrafting(false);
        setDraftPipelineStatus({ visible: false, stage: null, stagesDone: [] });
        toast.error('Draft is taking longer than expected. It will appear in the history once complete — refresh in a few minutes.');
      }
    }, 6000);
  };

  const handleTranslate = async () => {
    if (isTranslating) return;
    setIsTranslating(true);
    try {
      const response = await axios.post(`${API}/ai/translate/${docId}`);
      setDocument(prev => ({ ...prev, translated_text: response.data.translated_text }));
      setShowTranslated(true);
      toast.success(response.data.cached ? 'Translation loaded' : 'Translation complete!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Translation failed');
    } finally {
      setIsTranslating(false);
    }
  };

  const handleReprocessHindi = async () => {
    if (isReprocessingHindi) return;
    setIsReprocessingHindi(true);
    try {
      const response = await axios.post(`${API}/documents/${docId}/reprocess-hindi`);
      setDocument(prev => ({ ...prev, status: 'processing', processing_stage: 'queued' }));
      toast.info(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start Hindi OCR');
    } finally {
      setIsReprocessingHindi(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (isDownloadingPdf) return;
    setIsDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/documents/${docId}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = document?.filename || 'document.pdf';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download PDF. The original file may not be available.');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handleResearch = async () => {
    if (!researchQuery.trim() || isResearching || !isAdmin) return;
    setIsResearching(true);
    try {
      const response = await axios.post(`${API}/ai/research`, {
        doc_id: docId,
        query: researchQuery
      });
      setResearchResults(prev => [response.data, ...prev]);
      setResearchQuery('');
      toast.success('Research completed!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Research failed');
    } finally {
      setIsResearching(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const draftTypes = [
    { 
      id: 'reply', 
      label: 'Draft Reply', 
      icon: MessageSquare, 
      description: 'Formal point-by-point response to the notice',
      color: 'bg-blue-500'
    },
    { 
      id: 'summary_for_management', 
      label: 'Management Summary', 
      icon: Briefcase, 
      description: 'Executive briefing for client leadership',
      color: 'bg-purple-500'
    },
    { 
      id: 'legal_brief', 
      label: 'Legal Brief', 
      icon: BookOpen, 
      description: 'Detailed legal analysis with precedents',
      color: 'bg-green-500'
    },
    { 
      id: 'writ_petition', 
      label: 'Writ Petition (Art. 226/227)', 
      icon: Scale, 
      description: 'File a writ petition to the High Court',
      color: 'bg-red-500'
    },
    { 
      id: 'scn_reply_cgst_sgst', 
      label: 'SCN Reply (CGST/SGST)', 
      icon: FileText, 
      description: 'Reply to SCN under CGST/SGST Act, 2017',
      color: 'bg-indigo-500'
    },
    { 
      id: 'gst_appeal', 
      label: 'GST Appeal', 
      icon: ArrowRight, 
      description: 'Draft appeal in GST matters',
      color: 'bg-teal-500'
    },
    { 
      id: 'retraction_statement', 
      label: 'Statement Retraction', 
      icon: AlertCircle, 
      description: 'Retract statements recorded under coercion',
      color: 'bg-orange-500'
    },
    { 
      id: 'adjournment_letter', 
      label: 'Adjournment Letter', 
      icon: Clock, 
      description: 'Seek adjournment for summons received',
      color: 'bg-cyan-500'
    }
  ];

  const suggestedResearchQueries = [
    "Recent case law on similar notices",
    "Penalties and interest provisions",
    "Successful defense strategies",
    "Time limitations for response"
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-slate-600">Loading document...</p>
        </div>
      </div>
    );
  }

  // ==================== REGULAR USER VIEW ====================
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <AppSidebar user={user} onLogout={logout} active="documents" />
        
        <main className="ml-72 p-8" data-testid="document-view">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-6">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/dashboard')}
                className="mb-4 hover:bg-slate-100"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to My Notices
              </Button>
              
              <h1 className="text-2xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                {document?.filename}
              </h1>
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  {document?.page_count} pages
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Uploaded {new Date(document?.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>

            {/* Status Card */}
            <Card className="border-slate-200 mb-6 overflow-hidden">
              <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-6 text-white">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                    <Clock className="w-7 h-7" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Notice Under Review
                    </h2>
                    <p className="text-white/90">
                      Our legal team is reviewing your notice
                    </p>
                  </div>
                </div>
              </div>
              <CardContent className="p-6">
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                    <h3 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
                      <HelpCircle className="w-5 h-5" />
                      What happens next?
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
                        <div>
                          <p className="font-medium text-blue-900">Expert Review</p>
                          <p className="text-sm text-blue-700">Our team of legal experts will thoroughly analyze your notice</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
                        <div>
                          <p className="font-medium text-blue-900">Case Law Research</p>
                          <p className="text-sm text-blue-700">We'll find relevant precedents and legal provisions to support your case</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
                        <div>
                          <p className="font-medium text-blue-900">Advisory & Next Steps</p>
                          <p className="text-sm text-blue-700">We'll contact you with our recommendations and help draft your reply</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contact Section */}
                  <div className="bg-[#FDFBF7] rounded-xl p-6 border border-slate-200">
                    <h3 className="font-semibold text-slate-900 mb-4">Need Urgent Assistance?</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#1E3A8A]/10 rounded-lg flex items-center justify-center">
                          <Mail className="w-5 h-5 text-[#1E3A8A]" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Email us</p>
                          <p className="text-slate-700 font-medium">Contact us for details</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#F59E0B]/10 rounded-lg flex items-center justify-center">
                          <Phone className="w-5 h-5 text-[#F59E0B]" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Call us</p>
                          <p className="text-slate-700 font-medium">Available on request</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <p className="text-sm text-slate-600">
                        <strong>Leadership:</strong> Ashok Kumar Singh, Sole Proprietor of AKS Tax & Legal Consultancy, will personally oversee your case.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Document Preview */}
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <FileText className="w-5 h-5 text-[#1E3A8A]" />
                      Your Uploaded Notice
                      {document?.detected_language === 'hindi' && (
                        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-normal">
                          Hindi
                        </span>
                      )}
                    </CardTitle>
                    <CardDescription>
                      Extracted text from your PDF document
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] pr-4">
                  {document?.status === 'processing' ? (
                    <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                      <div className="w-12 h-12 border-4 border-[#1E3A8A]/20 border-t-[#1E3A8A] rounded-full animate-spin mb-4"></div>
                      <p className="font-medium text-slate-700 mb-1">Processing Your Document...</p>
                      <p className="text-sm text-slate-500">Extracting text from {document?.page_count} pages. This may take a few minutes for scanned documents.</p>
                    </div>
                  ) : document?.status === 'failed' ? (
                    <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                      <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
                      <p className="font-medium text-red-700 mb-1">Processing Failed</p>
                      <p className="text-sm text-slate-500">The document could not be processed. Please try re-uploading.</p>
                    </div>
                  ) : (
                    <div className="prose-legal whitespace-pre-wrap text-sm bg-slate-50 p-4 rounded-lg border border-slate-100">
                      {document?.extracted_text || 'No text could be extracted from this document.'}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  // ==================== ADMIN VIEW ====================
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <AppSidebar user={user} onLogout={logout} active="documents" />
      
      <main className="ml-72 p-8" data-testid="document-view">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              className="mb-4 hover:bg-slate-100"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to All Notices
            </Button>
            
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {document?.filename}
                </h1>
                <div className="flex items-center gap-4 text-sm text-slate-500 flex-wrap">
                  <span className="flex items-center gap-1">
                    <FileText className="w-4 h-4" />
                    {document?.page_count} pages
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {new Date(document?.created_at).toLocaleDateString()}
                  </span>
                  {document?.user_email && (
                    <span className="flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1 rounded-full font-medium">
                      <User className="w-4 h-4" />
                      Client: {document.user_name || document.user_email}
                    </span>
                  )}
                  {document?.status === 'completed' ? (
                    <span className="badge-approved flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Reviewed
                    </span>
                  ) : (
                    <span className="badge-pending flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Pending Review
                    </span>
                  )}
                </div>
              </div>
              
              {/* Document Action Buttons */}
              <div className="flex items-center gap-3 mt-4 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadPdf}
                  disabled={isDownloadingPdf || !document?.storage_path}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid="download-pdf-btn"
                >
                  {isDownloadingPdf ? (
                    <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-700 rounded-full animate-spin mr-2" />
                  ) : (
                    <FileDown className="w-4 h-4 mr-2" />
                  )}
                  Download Original PDF
                </Button>
                {document?.ocr_language !== 'eng+hin' && document?.status !== 'processing' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleReprocessHindi}
                    disabled={isReprocessingHindi || document?.status === 'processing'}
                    className="border-orange-200 text-orange-700 hover:bg-orange-50"
                    data-testid="hindi-ocr-btn"
                  >
                    {isReprocessingHindi ? (
                      <div className="w-4 h-4 border-2 border-orange-300 border-t-orange-700 rounded-full animate-spin mr-2" />
                    ) : (
                      <Languages className="w-4 h-4 mr-2" />
                    )}
                    Re-OCR with Hindi
                  </Button>
                )}
                {document?.ocr_language === 'eng+hin' && (
                  <span className="text-xs bg-green-100 text-green-700 px-3 py-1.5 rounded-full font-medium flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    Hindi OCR Applied
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-5 gap-6">
            {/* Left Panel - Document Text */}
            <div className="lg:col-span-2">
              <Card className="border-slate-200 sticky top-4">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <FileText className="w-5 h-5 text-[#1E3A8A]" />
                      Client's Notice
                      {document?.detected_language === 'hindi' && (
                        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-normal" data-testid="lang-badge-hindi">
                          Hindi Detected
                        </span>
                      )}
                    </CardTitle>
                    {document?.detected_language === 'hindi' && document?.status === 'text_extracted' && (
                      <div className="flex items-center gap-2">
                        {document?.translated_text ? (
                          <div className="flex items-center bg-slate-100 rounded-lg p-0.5" data-testid="lang-toggle">
                            <button 
                              onClick={() => setShowTranslated(false)}
                              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${!showTranslated ? 'bg-white shadow text-[#1E3A8A]' : 'text-slate-500'}`}
                              data-testid="toggle-original"
                            >
                              Original (Hindi)
                            </button>
                            <button
                              onClick={() => setShowTranslated(true)}
                              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${showTranslated ? 'bg-white shadow text-[#1E3A8A]' : 'text-slate-500'}`}
                              data-testid="toggle-translated"
                            >
                              English Translation
                            </button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleTranslate}
                            disabled={isTranslating}
                            className="text-xs border-orange-200 text-orange-700 hover:bg-orange-50"
                            data-testid="translate-btn"
                          >
                            {isTranslating ? (
                              <>
                                <div className="w-3 h-3 border-2 border-orange-300 border-t-orange-700 rounded-full animate-spin mr-1.5"></div>
                                Translating...
                              </>
                            ) : (
                              'Translate to English'
                            )}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[600px] pr-4">
                    {document?.status === 'processing' ? (
                      <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                        <div className="w-12 h-12 border-4 border-[#1E3A8A]/20 border-t-[#1E3A8A] rounded-full animate-spin mb-4"></div>
                        <p className="font-medium text-slate-700 mb-1">
                          {document?.processing_stage?.includes('hindi') ? 'Re-processing with Hindi OCR...' : 'Processing Document...'}
                        </p>
                        <p className="text-sm text-slate-500">
                          {document?.processing_stage?.includes('hindi')
                            ? `Hindi + English OCR on ${document?.page_count} pages. This takes longer than standard OCR.`
                            : `OCR is extracting text from ${document?.page_count} scanned pages. This may take a few minutes.`}
                        </p>
                        {document?.pages_processed > 0 && document?.total_pages_to_process > 0 && (
                          <div className="w-full max-w-xs mt-4">
                            <Progress value={(document.pages_processed / document.total_pages_to_process) * 100} className="h-2" />
                            <p className="text-xs text-slate-400 mt-1">{document.pages_processed}/{document.total_pages_to_process} pages</p>
                          </div>
                        )}
                      </div>
                    ) : document?.status === 'failed' ? (
                      <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
                        <p className="font-medium text-red-700 mb-1">Processing Failed</p>
                        <p className="text-sm text-slate-500">The document could not be processed. Please try re-uploading.</p>
                      </div>
                    ) : (
                      <div className="prose-legal whitespace-pre-wrap text-sm bg-slate-50 p-4 rounded-lg border border-slate-100">
                        {showTranslated && document?.translated_text 
                          ? document.translated_text 
                          : (document?.extracted_text || 'No text could be extracted from this document.')}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>

            {/* Right Panel - Admin Tools */}
            <div className="lg:col-span-3 space-y-6">
              
              {/* Summary Section */}
              <Card className="border-slate-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                        <Sparkles className="w-6 h-6" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                          AI Analysis Tools
                        </h2>
                        <p className="text-white/80 text-sm">
                          Generate summary and prepare client advisory
                        </p>
                      </div>
                    </div>
                    {document?.summary && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleSummarize}
                        className="text-white hover:bg-white/20"
                        data-testid="regenerate-summary-btn"
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Regenerate
                      </Button>
                    )}
                  </div>
                </div>
                
                <CardContent className="p-6">
                  {isSummarizing ? (
                    <div className="space-y-6 py-8">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-[#1E3A8A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Sparkles className="w-8 h-8 text-[#1E3A8A] animate-pulse" />
                        </div>
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">Analyzing Notice...</h3>
                        <p className="text-slate-500 mb-6">
                          Processing {document?.page_count} pages
                        </p>
                      </div>
                      <Progress value={summarizeProgress} className="h-2" />
                    </div>
                  ) : document?.summary ? (
                    <div className="space-y-4">
                      <div className="bg-[#FDFBF7] rounded-xl p-5 border border-slate-100">
                        <div className="flex items-start gap-3 mb-3">
                          <Lightbulb className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                          <h3 className="font-semibold text-slate-900">Notice Summary</h3>
                        </div>
                        <FormattedContent content={document.summary} />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(document.summary)}
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Summary
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadAsWord(document.summary, document.filename, 'Notice Summary', `Summary_${document.filename?.replace(/\.pdf$/i, '') || 'document'}`)}
                          data-testid="download-summary-btn"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download Word
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-[#1E3A8A]/5 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Sparkles className="w-8 h-8 text-[#1E3A8A]" />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">Generate Summary</h3>
                      <p className="text-slate-500 mb-4 text-sm">
                        Create an AI-powered summary to understand the key issues
                      </p>
                      <Button
                        onClick={handleSummarize}
                        className="btn-accent"
                        data-testid="summarize-btn"
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Summary
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Advisory Tools Tabs */}
              <Card className="border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Advisory Workflow</CardTitle>
                  <CardDescription>
                    Research case laws, prepare advisory, and draft replies for the client
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid grid-cols-3 w-full mb-6">
                      <TabsTrigger value="research" className="data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-white" data-testid="tab-research">
                        <Search className="w-4 h-4 mr-2" />
                        Research
                      </TabsTrigger>
                      <TabsTrigger value="draft" className="data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-white" data-testid="tab-draft">
                        <PenTool className="w-4 h-4 mr-2" />
                        Draft Reply
                      </TabsTrigger>
                      <TabsTrigger value="history" className="data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-white" data-testid="tab-history">
                        <FileCheck className="w-4 h-4 mr-2" />
                        History
                      </TabsTrigger>
                    </TabsList>

                    {/* Research Tab */}
                    <TabsContent value="research" className="space-y-4">
                      <div>
                        <label className="font-medium text-slate-900 mb-2 block text-sm">
                          Find Relevant Case Laws & Legal Provisions
                        </label>
                        <Textarea
                          value={researchQuery}
                          onChange={(e) => setResearchQuery(e.target.value)}
                          placeholder="E.g., Recent case law on GST input credit denial, FEMA penalty provisions..."
                          className="min-h-[80px] border-slate-200 focus:border-[#1E3A8A] focus:ring-[#1E3A8A]"
                          data-testid="research-query-input"
                        />
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        {suggestedResearchQueries.map((query, idx) => (
                          <button
                            key={idx}
                            onClick={() => setResearchQuery(query)}
                            className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-full transition-colors"
                          >
                            {query}
                          </button>
                        ))}
                      </div>
                      
                      <Button
                        onClick={handleResearch}
                        disabled={isResearching || !researchQuery.trim()}
                        className="w-full btn-primary"
                        data-testid="conduct-research-btn"
                      >
                        {isResearching ? (
                          <>
                            <div className="spinner w-4 h-4 mr-2"></div>
                            Researching...
                          </>
                        ) : (
                          <>
                            <Search className="w-4 h-4 mr-2" />
                            Find Case Laws
                          </>
                        )}
                      </Button>
                      
                      {researchResults.length > 0 && (
                        <div className="space-y-3 pt-4 border-t border-slate-100">
                          <h4 className="font-medium text-slate-900 text-sm">Research Results</h4>
                          {researchResults.slice(0, 2).map((research) => (
                            <div key={research.research_id} className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                              <p className="font-medium text-sm text-[#1E3A8A] mb-2">{research.query}</p>
                              <ScrollArea className="max-h-[280px] pr-2">
                                <FormattedContent content={research.response} />
                              </ScrollArea>
                              {research.citations?.length > 0 && (
                                <div className="mt-2 pt-2 border-t border-slate-200">
                                  <div className="flex flex-wrap gap-1">
                                    {research.citations.slice(0, 3).map((citation, idx) => (
                                      <a
                                        key={idx}
                                        href={citation.url || citation}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs bg-blue-50 text-[#1E3A8A] hover:bg-blue-100 px-2 py-1 rounded flex items-center gap-1"
                                      >
                                        <ExternalLink className="w-3 h-3" />
                                        Source {idx + 1}
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <div className="flex gap-2 mt-2">
                                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(research.response)} data-testid={`copy-research-active-${research.research_id}`}>
                                  <Copy className="w-3 h-3 mr-1" /> Copy
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => downloadAsWord(
                                    `**Research Query:** ${research.query}\n\n---\n\n${research.response}`,
                                    document?.filename,
                                    'Legal Research',
                                    `Research_${document?.filename?.replace(/\.pdf$/i, '') || 'document'}`
                                  )}
                                  data-testid={`download-research-active-${research.research_id}`}
                                >
                                  <Download className="w-3 h-3 mr-1" /> Download Word
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </TabsContent>

                    {/* Draft Tab */}
                    <TabsContent value="draft" className="space-y-4">
                      <div>
                        <h4 className="font-medium text-slate-900 mb-3 text-sm">Select Document Type</h4>
                        <div className="grid grid-cols-2 gap-2 max-h-[320px] overflow-y-auto pr-1">
                          {draftTypes.map((type) => (
                            <button
                              key={type.id}
                              onClick={() => setSelectedDraftType(type.id)}
                              className={`flex items-center gap-2.5 p-2.5 rounded-lg border text-left transition-all ${
                                selectedDraftType === type.id 
                                  ? 'border-[#1E3A8A] bg-[#1E3A8A]/5 ring-1 ring-[#1E3A8A]/20' 
                                  : 'border-slate-200 hover:border-slate-300'
                              }`}
                              data-testid={`draft-type-${type.id}`}
                            >
                              <div className={`w-9 h-9 ${type.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                                <type.icon className="w-4 h-4 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-slate-900 text-xs leading-tight">{type.label}</p>
                                <p className="text-[11px] text-slate-500 truncate">{type.description}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <label className="font-medium text-slate-900 mb-2 block text-sm">
                          Client Instructions / Special Notes
                        </label>
                        <Textarea
                          value={additionalInstructions}
                          onChange={(e) => setAdditionalInstructions(e.target.value)}
                          placeholder="E.g., Client wants to emphasize compliance history, focus on procedural issues..."
                          className="min-h-[80px] border-slate-200 focus:border-[#1E3A8A] focus:ring-[#1E3A8A]"
                          data-testid="additional-instructions"
                        />
                      </div>
                      
                      <Button
                        onClick={handleDraft}
                        disabled={isDrafting || !document?.summary}
                        className="w-full btn-primary"
                        data-testid="generate-draft-btn"
                      >
                        {isDrafting ? (
                          <>
                            <div className="spinner w-4 h-4 mr-2"></div>
                            Senior advocate pipeline running…
                          </>
                        ) : (
                          <>
                            <PenTool className="w-4 h-4 mr-2" />
                            Generate {draftTypes.find(t => t.id === selectedDraftType)?.label}
                          </>
                        )}
                      </Button>

                      {/* Agentic pipeline progress strip — visible during generation.
                          3 stages: Junior Advocate → Senior Counsel → Senior Advocate.
                          Each turns green as the underlying LLM completes its work. */}
                      {draftPipelineStatus.visible && (
                        <div className="rounded-lg border border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50 p-3" data-testid="draft-pipeline-progress">
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-700 mb-2">
                            Agentic Drafting Pipeline · ~4-6 minutes
                          </p>
                          <div className="flex items-center gap-2">
                            {[
                              { key: 'drafter', label: 'Junior Advocate', model: 'Claude Sonnet 4.5' },
                              { key: 'critic', label: 'Senior Counsel', model: 'GPT-5.2' },
                              { key: 'finalizer', label: 'Senior Advocate', model: 'Claude Opus 4.5' },
                            ].map((s, idx) => {
                              const isDone = draftPipelineStatus.stagesDone?.includes(s.key);
                              const isActive = draftPipelineStatus.stage === s.key && !isDone;
                              return (
                                <React.Fragment key={s.key}>
                                  <div className={`flex-1 rounded-md border-2 p-2 transition-all ${
                                    isDone ? 'border-green-400 bg-green-50' :
                                    isActive ? 'border-indigo-400 bg-white shadow-sm' :
                                    'border-slate-200 bg-white opacity-60'
                                  }`} data-testid={`pipeline-stage-${s.key}`}>
                                    <div className="flex items-center justify-between mb-0.5">
                                      <span className={`text-[10px] font-bold ${isDone ? 'text-green-700' : isActive ? 'text-indigo-700' : 'text-slate-400'}`}>
                                        {`①②③`[idx]} {s.label}
                                      </span>
                                      {isDone ? (
                                        <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                                      ) : isActive ? (
                                        <Loader2 className="w-3.5 h-3.5 text-indigo-500 animate-spin" />
                                      ) : null}
                                    </div>
                                    <p className={`text-[9px] ${isDone ? 'text-green-600' : isActive ? 'text-indigo-500' : 'text-slate-400'}`}>
                                      {s.model}
                                    </p>
                                    <p className={`text-[9px] mt-0.5 ${isDone ? 'text-green-600' : isActive ? 'text-indigo-500' : 'text-slate-400'}`}>
                                      {isDone ? 'Done' : isActive ? 'Working…' : 'Waiting'}
                                    </p>
                                  </div>
                                  {idx < 2 && (
                                    <div className={`w-3 h-0.5 ${draftPipelineStatus.stagesDone?.includes(s.key) ? 'bg-green-400' : 'bg-slate-200'}`} />
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </div>
                          {draftPipelineStatus.stage === 'done' && (
                            <p className="mt-2 text-xs text-green-700 font-medium text-center">
                              ✓ All stages complete — open the History tab to see the 3 views.
                            </p>
                          )}
                        </div>
                      )}

                      {!document?.summary && (
                        <p className="text-center text-xs text-amber-600 flex items-center justify-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Generate summary first
                        </p>
                      )}
                    </TabsContent>

                    {/* History Tab */}
                    <TabsContent value="history">
                      <ScrollArea className="h-[350px]">
                        {drafts.length === 0 && researchResults.length === 0 ? (
                          <div className="text-center py-8">
                            <FileCheck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                            <p className="text-slate-500 text-sm">No history yet</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {drafts.map((draft) => {
                              const p = draft.pipeline || {};
                              const isAgentic = !!(p.initial_draft || p.critique || p.final);
                              const isRunning = draft.status === 'running';
                              const isFailed = draft.status === 'failed';
                              const activeView = draftTab[draft.draft_id] || 'final';
                              const viewContent = activeView === 'initial'
                                ? (p.initial_draft || '(Junior advocate draft in progress…)')
                                : activeView === 'critique'
                                  ? (p.critique || '(Senior reviewer critique in progress…)')
                                  : (p.final || draft.content || '(Final draft in progress…)');
                              const downloadName = `${(DRAFT_TYPE_LABELS[draft.draft_type] || draft.draft_type).replace(/[^a-zA-Z0-9]/g, '_')}_${document?.filename?.replace(/\.pdf$/i, '') || 'document'}`;
                              return (
                                <div key={draft.draft_id} className="bg-slate-50 rounded-lg p-4 border border-slate-100" data-testid={`draft-card-${draft.draft_id}`}>
                                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                    <span className="font-medium text-slate-900 text-sm capitalize flex items-center gap-2 flex-wrap">
                                      <PenTool className="w-4 h-4" />
                                      {DRAFT_TYPE_LABELS[draft.draft_type] || draft.draft_type.replace(/_/g, ' ')}
                                      {isAgentic && (
                                        <span className="text-[10px] uppercase tracking-wider bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-1.5 py-0.5 rounded">
                                          Agentic
                                        </span>
                                      )}
                                      {p.mode === 'compact' && (
                                        <span
                                          className="text-[10px] uppercase tracking-wider bg-amber-100 border border-amber-300 text-amber-800 px-1.5 py-0.5 rounded"
                                          title="Initial draft was very large — skipped the 2nd critique pass to save time and cost"
                                          data-testid={`compact-badge-${draft.draft_id}`}
                                        >
                                          Compact
                                        </span>
                                      )}
                                      {p.mode === 'partial' && (
                                        <span
                                          className="text-[10px] uppercase tracking-wider bg-red-100 border border-red-300 text-red-800 px-1.5 py-0.5 rounded"
                                          title={p.warning || 'Pipeline completed partially'}
                                          data-testid={`partial-badge-${draft.draft_id}`}
                                        >
                                          Partial
                                        </span>
                                      )}
                                      {!isRunning && p.output_tokens_estimate ? (
                                        <span
                                          className="text-[10px] text-slate-500"
                                          title={`Total output across ${p.stages?.length || 0} LLM calls = ${p.output_chars_total?.toLocaleString() || 0} chars`}
                                          data-testid={`token-est-${draft.draft_id}`}
                                        >
                                          · ~{(p.output_tokens_estimate / 1000).toFixed(1)}K tokens
                                        </span>
                                      ) : null}
                                      {isRunning && (
                                        <span className="text-[11px] text-blue-600 flex items-center gap-1">
                                          <Loader2 className="w-3 h-3 animate-spin" /> Generating…
                                        </span>
                                      )}
                                      {isFailed && (
                                        <span className="text-[11px] text-red-600">Failed</span>
                                      )}
                                    </span>
                                    <span className="text-xs text-slate-500">
                                      {new Date(draft.created_at).toLocaleString()}
                                    </span>
                                  </div>

                                  {/* Warning banner for partial/compact modes — explains why this draft differs from a full 5-stage run */}
                                  {!isRunning && p.warning && (
                                    <div className={`text-[11px] rounded p-2 mb-2 border ${
                                      p.mode === 'partial'
                                        ? 'bg-red-50 border-red-200 text-red-800'
                                        : 'bg-amber-50 border-amber-200 text-amber-800'
                                    }`} data-testid={`pipeline-warning-${draft.draft_id}`}>
                                      <strong>{p.mode === 'partial' ? 'Partial completion: ' : 'Cost-saved: '}</strong>
                                      {p.warning}
                                    </div>
                                  )}

                                  {/* 3-Tab view: Initial Draft | Critique | Final */}
                                  {isAgentic && (
                                    <div className="flex gap-1 mb-2 bg-white rounded-md border border-slate-200 p-1" data-testid={`draft-tabs-${draft.draft_id}`}>
                                      {[
                                        { id: 'initial', label: 'Initial Draft', sub: 'Sonnet 4.5' },
                                        { id: 'critique', label: 'Critique', sub: 'GPT-5.2' },
                                        { id: 'final', label: 'Final', sub: 'Opus 4.5' },
                                      ].map(t => (
                                        <button
                                          key={t.id}
                                          onClick={() => setDraftTab(prev => ({ ...prev, [draft.draft_id]: t.id }))}
                                          className={`flex-1 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                                            activeView === t.id
                                              ? 'bg-[#1E3A8A] text-white shadow-sm'
                                              : 'text-slate-600 hover:bg-slate-100'
                                          }`}
                                          data-testid={`draft-tab-${t.id}-${draft.draft_id}`}
                                        >
                                          <div>{t.label}</div>
                                          <div className={`text-[9px] mt-0.5 ${activeView === t.id ? 'text-indigo-200' : 'text-slate-400'}`}>{t.sub}</div>
                                        </button>
                                      ))}
                                    </div>
                                  )}

                                  <ScrollArea className="max-h-[250px]">
                                    {isRunning && !viewContent.trim().replace(/[()…]/g, '') ? (
                                      <div className="text-center py-6 text-slate-500 text-sm">
                                        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                                        {activeView === 'initial' && 'Junior advocate is drafting…'}
                                        {activeView === 'critique' && 'Senior reviewer is critiquing…'}
                                        {activeView === 'final' && 'Senior advocate is finalising…'}
                                      </div>
                                    ) : (
                                      <FormattedContent content={viewContent} />
                                    )}
                                  </ScrollArea>

                                  <div className="flex gap-2 mt-2 flex-wrap">
                                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(viewContent)}>
                                      <Copy className="w-3 h-3 mr-1" /> Copy
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => downloadAsWord(
                                        viewContent,
                                        document?.filename,
                                        `${DRAFT_TYPE_LABELS[draft.draft_type] || draft.draft_type.replace(/_/g, ' ')}${isAgentic ? ` — ${{initial:'Initial Draft', critique:'Critique', final:'Final'}[activeView]}` : ''}`,
                                        `${downloadName}${isAgentic ? `_${activeView}` : ''}`,
                                      )}
                                      data-testid={`download-draft-${draft.draft_id}`}
                                    >
                                      <Download className="w-3 h-3 mr-1" /> Download Word
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                            {researchResults.map((research) => (
                              <div key={research.research_id} className="bg-amber-50 rounded-lg p-4 border border-amber-100">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-medium text-amber-900 text-sm flex items-center gap-2">
                                    <Search className="w-4 h-4" />
                                    Research
                                  </span>
                                  <span className="text-xs text-amber-700">
                                    {new Date(research.created_at).toLocaleString()}
                                  </span>
                                </div>
                                <p className="text-sm text-amber-800 font-medium mb-1">{research.query}</p>
                                <ScrollArea className="max-h-[200px]">
                                  <FormattedContent content={research.response} className="text-amber-700" />
                                </ScrollArea>
                                <div className="flex gap-2 mt-2">
                                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(research.response)}>
                                    <Copy className="w-3 h-3 mr-1" /> Copy
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => downloadAsWord(
                                      `**Research Query:** ${research.query}\n\n---\n\n${research.response}`,
                                      document?.filename,
                                      'Legal Research',
                                      `Research_${document?.filename?.replace(/\.pdf$/i, '') || 'document'}`
                                    )}
                                  >
                                    <Download className="w-3 h-3 mr-1" /> Download Word
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </ScrollArea>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DocumentView;
